// PlayFirst Parent — Signup. Creates a user with role='parent_self' and
// stores the JWT, then routes to /parent-app/onboarding. Mirrors the
// existing /api/auth/register flow so we don't fork auth logic.
import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, ArrowRight, Loader2 } from 'lucide-react';
import { PFP } from './styles';
import PartnerCodeEntry, { readStoredPartnerCode, clearStoredPartnerCode } from './PartnerCodeEntry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ParentAppSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: '', email: '', password: '', accept: false });
  const [busy, setBusy] = useState(false);

  // Pre-fill email from the ?email= query param (used by the "you paid
  // but haven't signed up yet" magic-link email so parents land here with
  // their Paystack email already filled in).
  useEffect(() => {
    const q = searchParams.get('email');
    if (q && !form.email) {
      setForm((f) => ({ ...f, email: q.trim().toLowerCase() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept) {
      toast.error('Please accept the disclaimer to continue');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        role: 'parent_self',
        data_processing_consent: false,
        consent_version: '2026-02',
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));

      // If the parent applied a partner discount code on the Pricing
      // page (or just above on this page), persist it to their account
      // now that they're authenticated. Best-effort — we don't block
      // signup if the apply call fails.
      const stored = readStoredPartnerCode();
      if (stored?.code) {
        try {
          await axios.post(
            `${API}/parent/apply-partner-code`,
            { code: stored.code },
            { headers: { Authorization: `Bearer ${res.data.token}` } },
          );
        } catch (e2) {
          // Don't surface — code attribution is best-effort.
          // eslint-disable-next-line no-console
          console.warn('Partner code apply failed', e2);
        }
        const redirectUrl = stored.redirect_url;
        clearStoredPartnerCode();
        toast.success('Welcome to PlayFirst Parent — discount applied');
        if (redirectUrl) {
          // Send them straight to the discounted Paystack link in a new tab,
          // and route the app to onboarding so they can come back to it.
          window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        }
        navigate('/parent-app/onboarding');
        return;
      }

      toast.success('Welcome to PlayFirst Parent');
      navigate('/parent-app/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={PFP.page}>
      <header className="px-4 sm:px-8 py-4 max-w-6xl mx-auto">
        <Link to="/parent-app" className="flex items-center gap-2 w-fit">
          <Heart className="w-6 h-6 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-xl text-[#3a2f4f]">PlayFirst</span>
          <span className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium ml-1">
            Parent
          </span>
        </Link>
      </header>

      <main className="max-w-md mx-auto px-4 py-8">
        <h1 className={`${PFP.h2} mb-2`}>Create your account</h1>
        <p className={`${PFP.sub} mb-8`}>
          We'll set up your child's Language Map in the next step.
        </p>

        <form onSubmit={handleSubmit} className={`${PFP.card} p-6 space-y-5`}>
          {/* Partner discount code — hoisted to the TOP of the form so a
              parent with a code sees it BEFORE filling in their details.
              Does not redirect on apply; we attribute it to the user
              after signup completes. */}
          <div
            className="rounded-2xl bg-gradient-to-br from-amber-50 via-white to-amber-50/40 border-2 border-dashed border-amber-300/70 p-4 -m-1"
            data-testid="parent-signup-partner-code-callout"
          >
            <p className="text-xs font-medium text-amber-900 mb-2 text-center">
              <span className="inline-block mr-1" aria-hidden="true">✨</span>
              Have a discount code from a partner? Enter it first.
            </p>
            <PartnerCodeEntry
              redirectOnApply={false}
              testIdPrefix="parent-signup-partner-code"
              className="!border-0 !bg-white/80"
            />
          </div>

          <div>
            <label className="block text-sm text-stone-600 mb-1">Your name</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              placeholder="Sarah"
              data-testid="parent-signup-name"
            />
          </div>
          <div>
            <label className="block text-sm text-stone-600 mb-1">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              placeholder="sarah@example.com"
              data-testid="parent-signup-email"
            />
          </div>
          <div>
            <label className="block text-sm text-stone-600 mb-1">Password</label>
            <input
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              placeholder="at least 8 characters"
              data-testid="parent-signup-password"
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-stone-600">
            <input
              type="checkbox"
              checked={form.accept}
              onChange={(e) => setForm({ ...form, accept: e.target.checked })}
              className="mt-0.5"
              data-testid="parent-signup-accept"
            />
            <span>
              I understand PlayFirst Parent is not a diagnostic tool. If I'm concerned
              about my child's communication, hearing, development or wellbeing, I will
              speak with a qualified health professional.
            </span>
          </label>

          <button
            type="submit"
            disabled={busy}
            className={PFP.btnPrimary + ' w-full inline-flex items-center justify-center gap-2'}
            data-testid="parent-signup-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {busy ? 'Creating account…' : 'Continue'}
          </button>

          <p className="text-center text-xs text-stone-500">
            Already have an account?{' '}
            <Link to="/parent-app/login" className="text-[#7C6F9C] hover:underline">
              Sign in
            </Link>
          </p>
        </form>
      </main>
    </div>
  );
}
